// apps/admin-ui/src/components/widgets/PendingContractReviews.tsx
// Dashboard widget showing pending contract reviews
// ALL RIGHTS RESERVED

import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import '../../styles/contract-reviews.css';
import '../styles/widgets.css';

// =============================================================================
// TYPES
// =============================================================================

interface ContractReview {
  id: string;
  versionTag: string;
  gitCommitHash: string;
  contractsChanged: Array<string | { name: string; status?: string; notes?: string }>;
  status: 'pending' | 'in_review' | 'approved' | 'rejected';
  reviewerName: string | null;
  createdAt: string;
}

interface PendingReviewsData {
  reviews: ContractReview[];
  totalPending: number;
}

export interface PendingContractReviewsProps {
  data?: PendingReviewsData;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatVersion(tag: string, hash: string): string {
  // Display version with short hash: "v1.0.0 (d463aac)"
  const shortHash = hash.slice(0, 7);
  const versionDisplay = tag.startsWith('v') ? tag : `v${tag}`;
  return `${versionDisplay} (${shortHash})`;
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return 'just now';
}

/** Extract contract name from either string or JSONB object */
function getContractName(c: string | { name: string }): string {
  if (typeof c === 'string') return c;
  if (c && typeof c === 'object' && c.name) return c.name;
  return '';
}

// =============================================================================
// COMPONENT
// =============================================================================

export function PendingContractReviews({ data: propData }: PendingContractReviewsProps) {
  const [data, setData] = useState<PendingReviewsData | null>(propData || null);
  const [loading, setLoading] = useState(!propData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (propData) {
      setData(propData);
      return;
    }

    async function fetchData() {
      try {
        setLoading(true);
        const response = await api.get<PendingReviewsData>('/api/v1/admin/contracts/reviews/pending/recent');
        setData(response);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load pending reviews');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [propData]);

  if (loading) {
    return (
      <div className="widget-pending-reviews loading">
        <div className="skeleton-line" style={{ width: '60%', height: '20px' }} />
        <div className="skeleton-line" style={{ width: '80%', height: '16px', marginTop: '12px' }} />
        <div className="skeleton-line" style={{ width: '70%', height: '16px', marginTop: '8px' }} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="widget-pending-reviews error">
        <p>Failed to load pending reviews</p>
      </div>
    );
  }

  const { reviews, totalPending } = data || { reviews: [], totalPending: 0 };

  return (
    <div className="widget-pending-reviews">
      {/* Header with badge */}
      <div className="pending-reviews-header">
        <span className="pending-count-badge" data-count={totalPending}>
          {totalPending}
        </span>
        <span className="pending-label">
          {totalPending === 1 ? 'version awaiting review' : 'versions awaiting review'}
        </span>
      </div>

      {/* Reviews list */}
      {reviews.length === 0 ? (
        <div className="pending-reviews-empty">
          <p>No pending reviews</p>
        </div>
      ) : (
        <ul className="pending-reviews-list">
          {reviews.map((review) => {
            const names = review.contractsChanged.map(getContractName).filter(Boolean);
            return (
              <li key={review.id} className="pending-review-item">
                <div className="review-version">
                  <code>{formatVersion(review.versionTag, review.gitCommitHash)}</code>
                </div>
                <div className="review-contracts">
                  {names.length > 0 ? (
                    <span className="contracts-list">
                      {names.slice(0, 2).join(', ')}
                      {names.length > 2 && (
                        <span className="more-contracts">
                          +{names.length - 2} more
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="no-contracts">No contracts listed</span>
                  )}
                </div>
                <div className="review-meta">
                  <span className="review-time">{formatTimeAgo(review.createdAt)}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default PendingContractReviews;
